import React, { useEffect, useMemo } from 'react';
import { Activity, Eye, Layers, PieChart, Scale, TrendingUp } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import HoldingsScreen from '../HoldingsScreen';
import TaxHarvestingScreen from '../TaxHarvestingScreen';
import PortfolioHubLayout, { PortfolioHubTab } from '../portfolio/PortfolioHubLayout';
import ModelTradingScreen from './ModelTradingScreen';
import DriftMonitorScreen from './DriftMonitorScreen';
import SleeveUMAScreen from './SleeveUMAScreen';
import { Module, useFeatureFlags } from '../../contexts/FeatureFlagContext';

type TabType = 'holdings' | 'construction' | 'rebalancing' | 'tax-alpha' | 'sleeve-uma';

const DEFAULT_TAB: TabType = 'holdings';

const RetailPortfolioHub: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasAITaxHarvesting, isModuleEnabled } = useFeatureFlags();
  const hasSleeveUMA = isModuleEnabled(Module.SLEEVE_UMA);

  const tabs = useMemo<PortfolioHubTab[]>(() => {
    return [
      { id: 'holdings', label: 'Holdings', icon: <Eye className="w-4 h-4" />, badge: 'View' },
      { id: 'construction', label: 'Construction', icon: <PieChart className="w-4 h-4" />, badge: 'Models' },
      { id: 'rebalancing', label: 'Rebalancing', icon: <Scale className="w-4 h-4" />, badge: 'Drift' },
      ...(hasAITaxHarvesting
        ? [{ id: 'tax-alpha', label: 'Tax Alpha', icon: <TrendingUp className="w-4 h-4" />, badge: 'Tax' }]
        : []),
      ...(hasSleeveUMA
        ? [{ id: 'sleeve-uma', label: 'Sleeve / UMA', icon: <Layers className="w-4 h-4" />, badge: 'UMA' }]
        : []),
    ];
  }, [hasAITaxHarvesting, hasSleeveUMA]);

  const validTabs = new Set(tabs.map(tab => tab.id as TabType));
  const requestedTab = searchParams.get('tab') as TabType | null;
  const activeTab = requestedTab && validTabs.has(requestedTab) ? requestedTab : DEFAULT_TAB;

  useEffect(() => {
    if (requestedTab !== activeTab) {
      setSearchParams({ tab: activeTab }, { replace: true });
    }
  }, [activeTab, requestedTab, setSearchParams]);

  const handleTabChange = (tabId: string) => {
    setSearchParams({ tab: tabId }, { replace: true });
  };

  return (
    <PortfolioHubLayout
      title="Portfolio Management"
      description="RIA workspace for model construction, household drift rebalancing, tax-aware management, and sleeve-based account oversight."
      segmentLabel="RIA"
      segmentTone="emerald"
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={handleTabChange}
      headerAside={
        <div className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-slate-900/60 px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
          <Activity className="w-4 h-4 text-emerald-400" />
          Unified Workflow Shell
        </div>
      }
    >
      {activeTab === 'holdings' && <HoldingsScreen />}
      {activeTab === 'construction' && <ModelTradingScreen />}
      {activeTab === 'rebalancing' && <DriftMonitorScreen />}
      {activeTab === 'tax-alpha' && <TaxHarvestingScreen inHub />}
      {activeTab === 'sleeve-uma' && <SleeveUMAScreen embedded />}
    </PortfolioHubLayout>
  );
};

export default RetailPortfolioHub;
